import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BlockRendererV2Component } from '../block-renderer-v2/block-renderer-v2.component';
import { AddBlockV2Component } from '../add-block-v2/add-block-v2.component';
import { AddBlockModalV2Component } from '../add-block-modal-v2/add-block-modal-v2.component';
import { SubjectComponentV2 } from '../../blocks-v2/subject-v2/subject-v2.component';
import { PreheaderComponentV2 } from '../../blocks-v2/preheader-v2/preheader-v2.component';
import { IBlockForSelect } from '../../../../utils/blocks-helper';
import { GAP_DEFAULT_SIZE } from '../../../../models/email-template/gap.interface';
import { AnnotationLayerV2Component } from '../review/annotation-layer-v2/annotation-layer-v2.component';
import { InlineEditSessionV2 } from '../inline-edit/inline-edit-session-v2.service';
import { Subject, takeUntil } from 'rxjs';

/**
 * Region 4 of the mockup: the center canvas. Renders every non-selected block
 * as pure preview (isPreview=true - the same mechanism the existing app-preview
 * route already uses), with an add-block gap between each pair.
 */
@Component({
  selector: 'app-preview-v2',
  imports: [
    CommonModule,
    BlockRendererV2Component,
    AddBlockV2Component,
    AddBlockModalV2Component,
    SubjectComponentV2,
    PreheaderComponentV2,
    AnnotationLayerV2Component,
  ],
  templateUrl: './preview-v2.component.html',
  styleUrl: './preview-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // One inline-editing session per canvas, shared by every [appInlineEdit]
  // field in it (see inline-edit/inline-edit-v2.directive.ts).
  providers: [InlineEditSessionV2],
})
export class PreviewV2Component implements OnChanges, OnDestroy {
  @ViewChild('canvasWrap') private canvasWrapRef!: ElementRef<HTMLElement>;

  @Input() blocks: any[] = [];
  @Input() allBlocks: any[] = [];
  @Input() selectedBlockId: string | null = null;
  @Input() deviceMode: 'desktop' | 'mobile' = 'desktop';
  @Input() blockSelect: IBlockForSelect[] = [];
  @Input() addBlockDisabled = false;
  @Input() showAddBlock = false;
  /** True while the "View" toggle is active - hides the add-block/gap-config
   *  UI and switches to the real view-gap spacing (see gapBeforeReadonly()),
   *  mirroring the mockup's own .preview-view-mode/.preview-edit-mode split. */
  @Input() isReadonlyMode = false;
  /** Review (Proof/Annotate) mode - the canvas then shows the viewed version's
   *  frozen blocks read-only, with comment pins (see app-annotation-layer-v2). */
  @Input() reviewMode: 'off' | 'proof' | 'annotate' = 'off';
  /** True in review mode while the template has no version yet. */
  @Input() reviewEmpty = false;
  @Input() languageLabel = 'EN';

  @Output() selectBlock = new EventEmitter<string>();
  @Output() blockSelectEvent = new EventEmitter<any>();
  /** Shaped for the base class's own onBlockDataUpdated({blockId, ...data}) -
   *  the parent just forwards it there directly, same as any other block
   *  field edit (see block-renderer-v2's own (blockData) chain). */
  @Output() blockDataChange = new EventEmitter<any>();
  @Output() pullVersion = new EventEmitter<void>();
  /** An inline field in the canvas got focus - select its block, the same way
   *  focusing its right-panel form does. */
  @Output() focusBlock = new EventEmitter<string>();
  /** An inline field in the canvas lost focus after its text changed - the
   *  block's right-panel form has to pick the new text up. */
  @Output() inlineEditCommit = new EventEmitter<string>();

  private readonly _destroy$ = new Subject<void>();

  constructor(private inlineEditSession: InlineEditSessionV2) {
    inlineEditSession.focused$.pipe(takeUntil(this._destroy$)).subscribe((blockId) => this.focusBlock.emit(blockId));
    inlineEditSession.committed$.pipe(takeUntil(this._destroy$)).subscribe((blockId) => this.inlineEditCommit.emit(blockId));
  }

  /** The gap's blockData to insert after - non-null while the single, shared
   *  add-block-modal-v2 is open (see add-block-v2's openAddBlock output). */
  protected addBlockModalContext: any = null;

  protected onOpenAddBlock(blockData: any): void {
    this.addBlockModalContext = blockData;
  }

  protected onAddBlockSelected(event: any): void {
    this.blockSelectEvent.emit(event);
    this.addBlockModalContext = null;
  }

  /** A click / Enter / Space on a canvas block toggles its selection - except
   *  inside one of its inline fields or image slots: focusing the field (or
   *  opening the slot's file picker) already selected the block (focusBlock),
   *  and typing a space or pressing Enter there must not toggle it off again. */
  protected onBlockActivate(event: Event, blockId: string): void {
    const target = event.target as Element | null;
    if (target?.closest?.('.cr-inline-edit[contenteditable], .cr-inline-media.is-editable')) {
      return;
    }
    this.selectBlock.emit(blockId);
  }

  /** In Edit mode a canvas link (CTA, image, badge) must not leave the editor -
   *  clicking it only selects its block / places the caret. View mode and
   *  Proof/Annotate (isReadonlyMode) keep links working. */
  protected onCanvasLinkClick(event: MouseEvent): void {
    if (!this.isReadonlyMode && (event.target as Element | null)?.closest?.('a[href]')) {
      event.preventDefault();
    }
  }

  /** A file dropped on the canvas anywhere but an image slot (appInlineMedia,
   *  which handles - and so cancels - its own drags) would make the browser
   *  open it in place of the editor, losing unsaved work. Refuse it instead:
   *  the cursor shows "not allowed" and the drop does nothing. In every mode -
   *  a near miss in View mode would navigate away just the same. */
  protected onCanvasFileDrag(event: DragEvent): void {
    if (event.defaultPrevented || !event.dataTransfer?.types.includes('Files')) {
      return;
    }
    event.preventDefault();
    if (event.type === 'dragover') {
      event.dataTransfer.dropEffect = 'none';
    }
  }

  /** Mirrors edit-block-comments-v2's own scroll-into-view: centers the
   *  selected block's canvas preview whenever the selection changes (sidebar
   *  click, canvas click, ...). A no-op for a non-always-visible block being
   *  newly selected - its row is removed from `blocks` while selected (see
   *  previewBlocks() in template-editor-v2.component.ts), so there's nothing
   *  to center here; only Subject/Preheader/always-visible blocks stay in
   *  the canvas while selected. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedBlockId'] && this.selectedBlockId) {
      this.scrollSelectedBlockIntoView(this.selectedBlockId);
    }
    // View mode and Proof/Annotate (both arrive as isReadonlyMode) show the
    // canvas read-only, so inline fields can't be typed into there.
    if (changes['isReadonlyMode']) {
      this.inlineEditSession.setEnabled(!this.isReadonlyMode);
    }
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private scrollSelectedBlockIntoView(blockId: string): void {
    requestAnimationFrame(() => {
      if (this.selectedBlockId !== blockId) {
        return;
      }
      const target = this.canvasWrapRef?.nativeElement.querySelector(
        `[data-block-id="${CSS.escape(blockId)}"]`
      );
      target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  /** Subject/Preheader are always-on system fields, shown here permanently so
   *  they're never hidden behind a click. Their full form lives in the right
   *  panel (see edit-block-comments-v2); both can also be typed into here. */
  protected get subjectBlock(): any {
    return this.allBlocks.find((b: any) => b?.name === 'subject');
  }

  protected get preheaderBlock(): any {
    return this.allBlocks.find((b: any) => b?.name === 'preheader');
  }

  /** The real content block that follows `block` in allBlocks - null past the
   *  last one. Used to decide whether the gap-config control after `block`
   *  has anything to configure a gap towards (no next block = no gap
   *  concept). Looked up in allBlocks (not the possibly-filtered `blocks`
   *  input) so it reflects the true next block even if that one is currently
   *  hidden from preview because it's open in the right panel. */
  protected nextRealBlock(block: any): any {
    const index = this.allBlocks.indexOf(block);
    return this.allBlocks[index + 1] ?? null;
  }

  /** The view-gap `block` applies to the space below it - lives on its own
   *  top-level `gap` field (BlockModel.gap, applies downward), defaulting to
   *  20px when unset. Edit-mode only - drives the small dropdown in
   *  add-block-v2, not the real spacing (see gapBeforeReadonly()). */
  protected gapSizeAfter(block: any): string {
    return String(block?.gap ?? GAP_DEFAULT_SIZE);
  }

  /** The real spacing to render before `block` in View mode: the PRECEDING
   *  real block's own gap field (it applies downward), or 0 if there's
   *  nothing real before `block` to space away from at all. */
  protected gapBeforeReadonly(block: any): number {
    const index = this.allBlocks.indexOf(block);
    const previous = index > 0 ? this.allBlocks[index - 1] : null;
    if (!previous || previous.name === 'subject' || previous.name === 'preheader') {
      return 0;
    }
    return Number(previous.gap ?? GAP_DEFAULT_SIZE);
  }

  /** Bubbles a gap-dropdown change up as a plain block-data patch, reusing
   *  the exact same shape/handler ({blockId, ...data} -> onBlockDataUpdated)
   *  every other field edit already goes through - no separate gap-specific
   *  mutation path needed. Reverting to the default (20px) sends `gap` as
   *  undefined rather than "20" - Object.assign still sets the key locally,
   *  but JSON.stringify drops undefined-valued keys when the template is
   *  actually saved, so a never-customized gap is never sent to the backend. */
  protected onGapSizeChange(block: any, size: string): void {
    this.blockDataChange.emit({
      blockId: block.blockId,
      gap: size === GAP_DEFAULT_SIZE ? undefined : size,
    });
  }

  protected get showReviewEmpty(): boolean { return this.reviewMode !== 'off' && this.reviewEmpty; }
}
